import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";

import { useFilterStore } from "./FilterStoreProvider";
import { FILTER_KEYS } from "../../../constants/constants";
import { GENDER_OPTIONS } from "../../../models/product";

import Accordion from "./components/Accordion";
import GenderFilter from "./components/GenderFilter";
import CategoryFilter from "./components/CategoryFilter";
import BrandFilter from "./components/BrandFilter";
import PriceFilter from "./components/PriceFilter";
import SizeFilter from "./components/SizeFilter";
import StatusFilter from "./components/StatusFilter";
import FilterTags from "../searchFilterTags/FilterTags";

import IconClose from "../../../assets/icon_close.svg";
import "./MobileFilterPanel.css";

const GET_CATEGORIES_FOR_FILTER = gql`
  query getCategoriesCached {
    getCategoriesCached(limit: 100) {
      name
      subcategories {
        id
        name
      }
    }
  }
`;

/**
 * 모바일용 필터 패널 컴포넌트
 * @param {object} props
 * @param {boolean} props.isOpen - 모달의 열림 상태
 * @param {function} props.onClose - 모달을 닫는 함수
 * @param {boolean} [props.hideBrandFilter=false] - 브랜드 필터 숨김 여부
 */
function MobileFilterPanel({ isOpen, onClose, hideBrandFilter = false }) {
  const tempFilters = useFilterStore((state) => state.tempFilters);
  const {
    handleTempFilterChange,
    applyFilters,
    resetTempFilters,
    clearTempFilters,
  } = useFilterStore((state) => state);

  const [fetchCategories, { data: categoryData }] = useLazyQuery(
    GET_CATEGORIES_FOR_FILTER,
  );
  const allCategories =
    categoryData && categoryData.getCategoriesCached
      ? categoryData.getCategoriesCached
      : [];

  useEffect(() => {
    if (isOpen) {
      // 1. 모달이 열리면 현재 필터 값으로 임시 필터 초기화
      resetTempFilters();
      // 2. 카테고리 데이터 가져오기
      fetchCategories();
      // 3. Body 스크롤 잠금
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    // 컴포넌트 언마운트 시 스크롤 복원
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, resetTempFilters, fetchCategories]);

  // --- Event Handlers --- //
  const handleApply = useCallback(() => {
    applyFilters();
    onClose();
  }, [applyFilters, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // 각 필터 변경을 처리하는 핸들러들을 간소화하고 메모이징합니다.
  const updateTempFilters = useCallback(
    (newValues) => {
      handleTempFilterChange(newValues);
    },
    [handleTempFilterChange],
  );

  const handleStatusChange = useCallback(
    ({ isSelling, condition }) => {
      updateTempFilters({ isSelling, condition });
    },
    [updateTempFilters],
  );

  const handleRemoveFilter = useCallback(
    (filterType, value) => {
      let newValues = {};
      switch (filterType) {
        case FILTER_KEYS.GENDER:
          newValues = { gender: "" };
          break;
        case FILTER_KEYS.SUBCATEGORY_IDS:
          newValues = {
            subcategoryIds: tempFilters.subcategoryIds.filter(
              (id) => id !== value,
            ),
          };
          break;
        case FILTER_KEYS.BRANDS:
          newValues = {
            brands: tempFilters.brands.filter((brand) => {
              const brandName = typeof brand === "string" ? brand : brand.name;
              const valueName = typeof value === "string" ? value : value.name;
              return brandName !== valueName;
            }),
          };
          break;
        case "price":
          newValues = { priceMin: null, priceMax: null };
          break;
        case FILTER_KEYS.SIZE_VALUE_IDS: {
          const removeIds = new Set((value?.valueIds || []).map(Number));
          newValues = {
            [FILTER_KEYS.SIZE_VALUE_IDS]: (
              tempFilters[FILTER_KEYS.SIZE_VALUE_IDS] || []
            ).filter((id) => !removeIds.has(Number(id))),
          };
          break;
        }
        default:
          return;
      }
      updateTempFilters(newValues);
    },
    [tempFilters, updateTempFilters],
  );

  if (!isOpen) return null;

  return createPortal(
    <div className="MobileFilterPanel-overlay" onClick={handleCancel} role="dialog" aria-modal="true">
      <div className="MobileFilterPanel" onClick={(e) => e.stopPropagation()}>
        {/* --- Header --- */}
        <div className="MobileFilterPanel-header">
          <button
            type="button"
            className="MobileFilterPanel-close-button"
            onClick={handleCancel}
          >
            <img src={IconClose} alt="Close" />
          </button>
          <h3 className="MobileFilterPanel-title">필터</h3>
          <button
            type="button"
            className="MobileFilterPanel-reset-button"
            onClick={clearTempFilters}
          >
            초기화
          </button>
        </div>

        {/* --- Scrollable Content --- */}
        <div className="MobileFilterPanel-content-scrollable">
          <StatusFilter {...tempFilters} onChange={handleStatusChange} />

          <Accordion title="성별" initialOpen>
            <GenderFilter
              gender={tempFilters.gender || ""}
              options={GENDER_OPTIONS}
              onChange={(gender) => updateTempFilters({ gender })}
            />
          </Accordion>

          <Accordion title="카테고리">
            <CategoryFilter
              gender={tempFilters.gender || ""}
              categoriesFilter={tempFilters.categories || []}
              subcategoryIds={tempFilters.subcategoryIds || []}
              onChange={(values) => updateTempFilters(values)}
            />
          </Accordion>

          {!hideBrandFilter && (
            <Accordion title="브랜드">
              <BrandFilter
                selectedBrands={tempFilters.brands || []}
                onChange={(brands) => updateTempFilters({ brands })}
              />
            </Accordion>
          )}

          <Accordion title="가격">
            <PriceFilter
              priceMin={tempFilters.priceMin}
              priceMax={tempFilters.priceMax}
              onApply={(priceMin, priceMax) =>
                updateTempFilters({ priceMin, priceMax })
              }
              isMobile
            />
          </Accordion>

          <Accordion title="사이즈">
            <SizeFilter
              sizeValueIds={tempFilters.sizeValueIds || []}
              onChange={(sizeValueIds) => updateTempFilters({ sizeValueIds })}
            />
          </Accordion>
          <div style={{ height: "100px" }} />
        </div>

        {/* --- Footer --- */}
        <div className="MobileFilterPanel-footer">
          <div className="MobileFilterPanel-filter-tags">
            <FilterTags
              filters={tempFilters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={clearTempFilters}
              categories={allCategories}
              isMobile
            />
          </div>
          <div className="MobileFilterPanel-apply-button-container">
            <button
              type="button"
              className="MobileFilterPanel-apply-button"
              onClick={handleApply}
            >
              적용하기
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

MobileFilterPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hideBrandFilter: PropTypes.bool,
};

export default MobileFilterPanel;
